/**
 * The `email` SourceClient (sources.md "email — a mailbox over IMAP").
 *
 *   locator   imap://<user>@<host lower>:<port>/<mailbox>
 *   secret    source:<id>:password   (missing on this device → secret_missing)
 *   fetch     runtime.mail.fetchSince(account, mailbox, cursor.uid, cursor.uidValidity, limit) — the web's
 *             MailTransport is the server bridge (sources/mail-bridge.ts); a native app speaks IMAP itself.
 *   cursor    { uidValidity, uid } — a changed uidValidity restarts from the newest messages.
 *   parse     postal-mime, here on the device: Subject → title; From display name (else address) → author;
 *             Date (else INTERNALDATE) → publishedAt; HTML part → html, else text part → text; cid: images and
 *             attachments dropped; extra { from, listId, uid }; threadKey = first References id, else Message-ID.
 *   id        Message-ID, else '<uidValidity>:<uid>'.
 *
 * Never changes the mailbox: the bridge selects read-only and fetches with BODY.PEEK.
 */

import PostalMime, { type Address, type Email } from 'postal-mime';
import type { EpochMs } from '../contracts/ids.js';
import type {
  EmailConfig, MailAccount, RawItem, RawMail, SecretKey, SourceClient, SourceFetchContext, SourceFetchResult, SourceInput, SourceResolution,
} from '../contracts/sources.js';
import { DigesterError, toDigesterError } from '../kernel/errors.js';
import { sourceId } from '../kernel/ids.js';
import { foldText } from '../kernel/text.js';

/** Messages per bridge request (sources.md "email": `fetchSince(…, 50)`). */
export const EMAIL_FETCH_LIMIT = 50;

/** Default ports per security mode (IMAPS, IMAP + STARTTLS). */
const DEFAULT_PORTS: Readonly<Record<EmailConfig['security'], number>> = { tls: 993, starttls: 143, none: 143 };

export interface EmailCursor {
  uidValidity: number | null;
  uid: number;
}

export function emailLocator(config: Pick<EmailConfig, 'user' | 'host' | 'port' | 'mailbox'>): string {
  return `imap://${config.user}@${config.host.toLowerCase()}:${config.port}/${config.mailbox}`;
}

export function emailPasswordKey(id: string): SecretKey {
  return `source:${id}:password`;
}

function readCursor(cursor: Record<string, unknown> | null): EmailCursor {
  const uid = typeof cursor?.uid === 'number' && Number.isInteger(cursor.uid) && cursor.uid >= 0 ? cursor.uid : 0;
  const uidValidity = typeof cursor?.uidValidity === 'number' && Number.isFinite(cursor.uidValidity) ? cursor.uidValidity : null;
  return { uid, uidValidity };
}

/** '<abc@x>' → 'abc@x'. */
function bare(id: string): string {
  return id.trim().replace(/^<+/, '').replace(/>+$/, '').trim();
}

/** The first message id of a References header, if any. */
function firstReference(references: string | undefined): string | null {
  if (!references) return null;
  const match = /<([^<>]+)>/.exec(references);
  if (match?.[1]) return match[1].trim();
  const first = references.trim().split(/\s+/)[0];
  return first ? bare(first) : null;
}

function header(email: Email, key: string): string | null {
  const h = email.headers.find((x) => x.key === key);
  return h ? h.value.trim() : null;
}

function mailboxOf(address: Address | undefined): { name: string; address: string } | null {
  if (!address) return null;
  if (address.group !== undefined) {
    const first = address.group[0];
    return { name: address.name || first?.name || '', address: first?.address ?? '' };
  }
  return { name: address.name, address: address.address };
}

/** Remove images that point into the message itself (cid:); the normaliser would drop them anyway. */
function dropCidImages(html: string): string {
  return html.replace(/<img\b[^>]*\bsrc\s*=\s*(["']?)\s*cid:[^>]*>/gi, '');
}

function passesFromFilter(filter: readonly string[] | undefined, from: { name: string; address: string } | null): boolean {
  const needles = (filter ?? []).map(foldText).filter((s) => s !== '');
  if (needles.length === 0) return true;
  if (!from) return false;
  const hay = foldText(`${from.name} ${from.address}`);
  return needles.some((n) => hay.includes(n));
}

/** One fetched message → a RawItem (null when the From filter excludes it). Exported for tests. */
export async function mailToItem(mail: RawMail, uidValidity: number, fromFilter?: readonly string[]): Promise<RawItem | null> {
  let email: Email;
  try {
    email = await PostalMime.parse(mail.rfc822);
  } catch (error) {
    throw new DigesterError('parse_failed', `Message ${mail.uid} could not be parsed: ${toDigesterError(error).message}`);
  }
  const from = mailboxOf(email.from);
  if (!passesFromFilter(fromFilter, from)) return null;

  const messageId = email.messageId ? bare(email.messageId) : '';
  const dateMs = email.date ? Date.parse(email.date) : Number.NaN;
  const publishedAt = (Number.isFinite(dateMs) ? dateMs : mail.internalDate) as EpochMs;
  const extra: Record<string, unknown> = { from: from?.address ?? null, listId: header(email, 'list-id'), uid: mail.uid };
  if (mail.truncated) extra.truncated = true;

  const item: RawItem = {
    externalId: messageId !== '' ? messageId : `${uidValidity}:${mail.uid}`,
    publishedAt,
    extra,
  };
  const subject = (email.subject ?? '').replace(/\s+/g, ' ').trim();
  if (subject !== '') item.title = subject;
  const author = from ? (from.name.trim() || from.address.trim()) : '';
  if (author !== '') item.author = author;
  const html = email.html && email.html.trim() !== '' ? dropCidImages(email.html) : '';
  if (html.trim() !== '') item.html = html;
  else if (email.text && email.text.trim() !== '') item.text = email.text;
  else if (item.title === undefined) item.text = '';
  const thread = firstReference(email.references) ?? (messageId !== '' ? messageId : null);
  if (thread) item.threadKey = thread;
  return item;
}

function configFrom(input: SourceInput<'email'>): EmailConfig {
  const c = input.config ?? {};
  const issues: Array<{ path: string; message: string }> = [];
  const security = c.security ?? 'tls';
  if (security !== 'tls' && security !== 'starttls' && security !== 'none') issues.push({ path: 'config.security', message: 'tls, starttls or none' });
  const host = (c.host ?? '').trim();
  if (host === '' || /[\s/@]/.test(host)) issues.push({ path: 'config.host', message: 'the IMAP server name, e.g. imap.example.com' });
  const port = c.port ?? DEFAULT_PORTS[security === 'starttls' ? 'starttls' : 'tls'];
  if (!Number.isInteger(port) || port <= 0 || port > 65535) issues.push({ path: 'config.port', message: 'a port number' });
  const user = (c.user ?? input.address).trim();
  if (user === '') issues.push({ path: 'config.user', message: 'the mailbox user name' });
  const mailbox = (c.mailbox ?? 'INBOX').trim() || 'INBOX';
  if (issues.length > 0) throw new DigesterError('invalid_input', 'The mailbox settings are incomplete', { issues });
  const config: EmailConfig = { kind: 'email', host, port, security, user, mailbox };
  const fromFilter = (c.fromFilter ?? []).map((s) => s.trim()).filter((s) => s !== '');
  if (fromFilter.length > 0) config.fromFilter = fromFilter;
  return config;
}

export const emailClient: SourceClient<'email'> = {
  kind: 'email',
  defaultPullMinutes: 15,

  async resolve(input: SourceInput<'email'>): Promise<SourceResolution<'email'>> {
    const config = configFrom(input);
    const locator = emailLocator(config);
    return {
      kind: 'email',
      locator,
      name: (config.mailbox.toUpperCase() === 'INBOX' ? config.user : `${config.user} · ${config.mailbox}`).slice(0, 80),
      config,
      requiredSecrets: [emailPasswordKey(sourceId('email', locator))],
    };
  },

  async fetch(ctx: SourceFetchContext<'email'>): Promise<SourceFetchResult> {
    const { runtime, source } = ctx;
    if (!runtime.mail) throw new DigesterError('unsupported', 'Mailboxes are not reachable on this runtime (sign in to use the mail bridge).');
    const password = await runtime.secrets.get(emailPasswordKey(source.id));
    if (password === null || password === '') throw new DigesterError('secret_missing', 'Add the mailbox password on this device.');
    const config = source.config;
    const account: MailAccount = { host: config.host, port: config.port, security: config.security, user: config.user, password };
    const cursor = readCursor(ctx.cursor);
    const limit = Math.max(1, Math.min(ctx.maxItems, EMAIL_FETCH_LIMIT));
    const result = await runtime.mail.fetchSince(account, config.mailbox, cursor.uid, cursor.uidValidity, limit);

    // A changed uidValidity means the bridge answered from the newest messages: the old uid means nothing.
    const sameValidity = cursor.uidValidity !== null && cursor.uidValidity === result.uidValidity;
    let highest = sameValidity ? cursor.uid : 0;
    const items: RawItem[] = [];
    for (const mail of result.messages) {
      highest = Math.max(highest, mail.uid);
      try {
        const item = await mailToItem(mail, result.uidValidity, config.fromFilter);
        if (item && items.length < ctx.maxItems) items.push(item);
      } catch (error) {
        // One unreadable message never stops the mailbox; the uid still advances past it.
        console.log(`[email] skipped uid ${mail.uid}: ${toDigesterError(error).code}`);
      }
    }
    const next: EmailCursor = { uidValidity: result.uidValidity, uid: highest };
    return { items, cursor: { ...next } };
  },
};
